import * as React from 'react';
import {v4} from 'uuid';
import {buildTextInput} from '../../../libs/helpers';
import {UploadParams} from '../edi-files/EdiFilesInterfaces';
import {Company} from '../../shared/CommonInterfaces';
import {GenericErrorDisplay} from '../../shared/GenericErrorDisplay';
import FileStorageService from '../file-storage/FileStorageService';
import EdiFilesService from '../edi-files/EdiFilesService';
import EdiFilesServiceV2 from '../edi-files/EdiFilesServiceV2';
import ShipperUserEmailSelect from './ShipperUserEmailSelect';
import {ShipperUsersData} from './ShipperInfoInterfaces';
import ShipperInfoService from './ShipperInfoService';
import {
  csvBulkCancellationHeaders,
  csvBulkCancellationInstructions,
  csvBulkCancellationTemplatePath
} from './helpers/OutboundOrderCsv';
import {instructionsTableHeaders} from './helpers/OutboundOrdersUploadInstructions';
import CsvBulkUploadModalOMS, {BeforeOpenHandlerCallback, UploadHandlerCallback} from './generic/CsvBulkUploadModalOMS';

interface Props {
  authenticityToken: string;
  showUploadModal: boolean;
  currentCompany: Company;
  toggleUploadModal: () => void;
}

// TODO make spec tests once we're closer to turning this on
const OutboundOrdersBulkCancelModal: React.FC<Props> = (props: Props) => {
  const shipperInfoService: ShipperInfoService = new ShipperInfoService();
  const fileStorageService: FileStorageService = new FileStorageService();
  const v2EdiFilesService: EdiFilesServiceV2 = new EdiFilesServiceV2();
  const ediFilesService: EdiFilesService = new EdiFilesService(props.authenticityToken);

  const [flexeEmailUsername, setFlexeEmailUsername] = React.useState<string>('');
  const [shipperEmailAddress, setShipperEmailAddress] = React.useState<string>('');
  const [shipperUserList, setShipperUserList] = React.useState<ShipperUsersData[]>([]);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  const getShipperUserList = async (callback: BeforeOpenHandlerCallback) => {
    try {
      const userData = await shipperInfoService.getShipperUsers(props.currentCompany.id.toString());
      setShipperUserList(userData.data);
      callback([]);
    } catch (err) {
      const errors: GenericErrorDisplay[] = [];
      errors.push({header: `Error fetching shipper user emails. ${err}`});
      callback(errors);
    }
  };

  const handleUpload = async (params: UploadParams, type: string, callback: UploadHandlerCallback) => {
    const responseErrors: GenericErrorDisplay[] = [];
    if (!flexeEmailUsername && !shipperEmailAddress) {
      responseErrors.push({header: 'Please input at least one email address'});
    } else {
      setIsUploading(true);

      const fileKey = `outbound_cancel_${v4()}`;
      const fileStorageResponse = await fileStorageService.uploadFile(params.file, fileKey);

      if (!fileStorageResponse) {
        responseErrors.push({header: 'Storing file failed. Please try again and contact Flexe'});
      } else if (fileStorageResponse.errors && fileStorageResponse.errors.length > 0) {
        const errorDetails = ediFilesService.processErrors(fileStorageResponse.errors);
        responseErrors.push({
          header: 'Unexpected error while attempting to store the file:',
          details: errorDetails
        });
      } else {
        const flexeEmail = flexeEmailUsername ? flexeEmailUsername.trim() + '@flexe.com' : flexeEmailUsername;
        const emailList = [flexeEmail, shipperEmailAddress].filter((n) => n).join(',');
        const metadata = {
          email_distribution_list: emailList
        };
        const ediFileResponse = await v2EdiFilesService.createEdiFile(type, fileKey, metadata);
        if (!ediFileResponse) {
          responseErrors.push({header: 'Creating EDI file failed. Please try again and contact Flexe'});
        } else if (ediFileResponse.errors && ediFileResponse.errors.length > 0) {
          const ediErrorDetails = ediFilesService.processErrors(ediFileResponse.errors);
          responseErrors.push({
            header: 'Unexpected error while attempting to create the edi file',
            details: ediErrorDetails
          });
        } else {
          props.toggleUploadModal();
        }
      }
    }
    callback(responseErrors);
    setIsUploading(false);
    setFlexeEmailUsername('');
    setShipperEmailAddress('');
  };

  return (
    <CsvBulkUploadModalOMS
      id="outbound-order-bulk-cancel-modal"
      title="Cancel Orders From a CSV"
      actionDescription="cancel orders"
      show={props.showUploadModal}
      templatePath={csvBulkCancellationTemplatePath}
      toggleModal={props.toggleUploadModal}
      instructionsTableHeaders={instructionsTableHeaders}
      instructionsTableRows={csvBulkCancellationInstructions}
      uploading={isUploading}
      handleUpload={handleUpload}
      handleBeforeOpen={getShipperUserList}
      headers={csvBulkCancellationHeaders}
      fileUploadType="incoming_csv_bulk_order_cancel" // This is arbitrary since we're not using backwards compatibility
      disableSubmit={false}
    >
      <div>
        <p>
          This process can take a few minutes to complete. Once finished, a report with links to cancellation results
          will be sent to up to two users. These can be Flexe or shipper users. Please select the user(s) to receive the
          report. At least one user is required.
        </p>
        <p>
          You can cancel by just Flexe Order ID, just External Order ID, or Flexe Order ID + Flexe Order Line ID. Empty
          columns should be removed completely.
        </p>
        <span>
          <ShipperUserEmailSelect
            onUserEmailChange={setShipperEmailAddress}
            selectedUserEmail={shipperEmailAddress}
            allUserEmailData={shipperUserList}
          />
        </span>
        <span className="upload-choice-spacer">or</span>
        <span>
          {buildTextInput(
            'someone',
            flexeEmailUsername,
            (v: string) => setFlexeEmailUsername(v),
            '*Flexe email address to send cancellation summary to'
          )}
          <span className="at-flexe">@flexe.com</span>
        </span>
      </div>
    </CsvBulkUploadModalOMS>
  );
};

export default OutboundOrdersBulkCancelModal;
